import { ToastContextDispatch, UserInfoContext } from "Context/CommonContext";
import TodayScheduledLectureMain from "Pages/Dashboard/TodayScheduledLectureMain";
import { useContext, useEffect, useMemo, useState, useRef } from "react";
import { Card, Col, Spinner } from "reactstrap";
import { IUserInfo } from "utilFunctions/CommonInterfaces/userInterface";
import { pushToDataLayerForGTM } from "utilFunctions/helperFunctions/analytics";
import {
  BASE_URL,
  MY_BATCHES_LIST,
  SYLLABUS_COMPLIANCE_LIST,
  GET_FACULTY_PERFORMANCE_SEARCH,
} from "utilFunctions/Constants/urlConstants";
import {
  addDays,
  format,
  setHours,
  setMinutes,
  setSeconds,
  subDays,
} from "date-fns";
import { getAPIRequest } from "utilFunctions/Axios/AxiosRequestConfiguration";
import getRequestInterface from "utilFunctions/Axios/getRequestInterface";
import LectureCountCard from "Pages/Dashboard/LectureCountCard";
import { useGetApi } from "utilFunctions/CustomHooks/useGetAPI";
import Typography from "Components/CommonComponents/Typography";
import { useNavigate } from "react-router-dom";
import MyBatchesTableAntd from "Pages/MyBatchesAntd/MyBatchesTableAntd";
import TableLayoutAntd from "utilFunctions/shared/AntdSharedComponents/TableLayoutAntd";

const getDateOptions = () => {
  const now = new Date();
  const dateRange = [];
  for (let i = 0; i <= 11; i++) {
    const currentDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth() + 1;
    const monthName = currentDate.toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });
    const valueKey = `${year}-${month.toString().padStart(2, "0")}`;
    dateRange.push({
      label: monthName,
      value: valueKey,
      key: monthName,
    });
  }
  return dateRange;
};

const DashboardBatchesMain = ({ screen }: any) => {
  const navigate = useNavigate();
  const userInfo = useContext<IUserInfo>(UserInfoContext);
  const ToastDispatch = useContext<any>(ToastContextDispatch);
  const todayDate = format(new Date(), "yyyy-MM-dd");
  const nowDate = new Date();
  const month = String(nowDate.getMonth() + 1); // Months are 0-based, so we add 1
  const year = String(nowDate.getFullYear()).slice(-2); // Get the last two digits of the year
  const [dateRanges] = useState([
    {
      startDate: setSeconds(
        setMinutes(setHours(subDays(new Date(), 0), 18), 30),
        0
      ),
      endDate: setSeconds(
        setMinutes(setHours(addDays(new Date(), 0), 18), 29),
        0
      ),
      key: "selection",
    },
  ]);
  const [dateRanges2] = useState([
    {
      startDate: setSeconds(
        setMinutes(setHours(subDays(new Date(), 3), 18), 30),
        0
      ),
      endDate: setSeconds(
        setMinutes(setHours(addDays(new Date(), 3), 18), 29),
        0
      ),
      key: "selection",
    },
  ]);
  const getCurrentAcademicSession = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1; // 1-12
    return currentMonth >= 4
      ? `${currentYear}-${currentYear + 1}`
      : `${currentYear - 1}-${currentYear}`;
  };
  const dateFilterValues = useMemo(() => getDateOptions(), []);
  const [loading, setLoading] = useState(false);
  const [todaySchedules, setTodaySchedules] = useState<any>([]);
  const [BATCHData, setBATCHData] = useState<any>([]);
  const [batchDataLoaded, setBatchDataLoaded] = useState<boolean>(false);
  const batchRequestRef = useRef(0);
  const [sessionsList, setSessionsList] = useState<string[]>([]);
  const [sessionsListFFetching, setSessionsListFetching] =
    useState<boolean>(true);
  const [selectedSession, setSelectedSession] = useState<string>("");
  const filteredBatchData = useMemo(() => {
    if (!selectedSession) return BATCHData;
    return BATCHData.filter((batch: any) =>
      batch?.academicSession?.includes(selectedSession)
    );
  }, [BATCHData, selectedSession]);
  const sessionUnmarkedLectureCount = useMemo(
    () =>
      filteredBatchData.reduce(
        (sum: number, batch: any) => sum + (batch?.unMarkedLectureCount || 0),
        0
      ),
    [filteredBatchData]
  );
  const [facultyFeedbackData, setFacultyFeedbackData] = useState<any>([]);
  const [yearMonthForFeedback, setYearMonthForFeedback] = useState<any>(
    dateFilterValues[0].value
  );
  const [facultyFeedbackHomeData, setFacultyFeedbackHomeData] =
    useState<any>(null);
  const { makeRequest: GetBATCHDATA, loading: isBatchLoader } = useGetApi(
    (data) => {
      setBATCHData(data?.data?.batchResposeList || []);
      const sessions = data?.data?.sessions || [];
      setSessionsList(sessions);
      setSessionsListFetching(false);
      const currentSession = getCurrentAcademicSession();
      const defaultSession = sessions.includes(currentSession)
        ? currentSession
        : sessions[0] || "";
      setSelectedSession((prev) => prev || defaultSession);
    },
    (error) => {
      setBATCHData([]);
      if (
        error &&
        error["response"] &&
        error["response"]["data"] &&
        error["response"]["data"]["internalCode"] &&
        error["response"]["data"]["internalCode"] != "1008"
      ) {
        ToastDispatch({
          isOpen: true,
          type: "Error",
          message: error.message,
        });
      } else if (
        error &&
        error["response"] &&
        error["response"]["data"] &&
        error["response"]["data"]["code"] == "NF"
      ) {
        ToastDispatch({
          isOpen: true,
          type: "Error",
          message: "No record found",
        });
      }
    }
  );
  const { makeRequest: GetFacultyFeedbackData } = useGetApi(
    (data) => {
      if (
        data["data"] &&
        data["data"] &&
        data["data"]["facultyFeedbackResponse"]
      ) {
        setFacultyFeedbackData(data["data"]["facultyFeedbackResponse"] || []);
        setFacultyFeedbackHomeData((prev: any) => {
          if (prev !== null) return prev;
          const response = data["data"]["facultyFeedbackResponse"];
          const overallRatingAvg = response?.[0]?.overallRatingAvg;
          return overallRatingAvg && overallRatingAvg !== 0
            ? overallRatingAvg
            : "NA";
        });
      } else {
        setFacultyFeedbackHomeData(null);
        setFacultyFeedbackData([]);
      }
    },
    (error) => {
      setFacultyFeedbackHomeData(null);
      setFacultyFeedbackData([]);
      if (error && error["message"]) {
        ToastDispatch({
          isOpen: true,
          type: "Error",
          message: error.message,
        });
      } else if (
        error &&
        error["response"] &&
        error["response"]["data"] &&
        error["response"]["data"]["code"] == "NF"
      ) {
        ToastDispatch({
          isOpen: true,
          type: "Error",
          message: "No record found",
        });
      }
    }
  );
  useEffect(() => {
    if (userInfo && userInfo.employeeId) {
      const requestIndex = ++batchRequestRef.current;
      setBatchDataLoaded(false);
      setSessionsListFetching(true);
      let url = BASE_URL + MY_BATCHES_LIST;
      url =
        url +
        "?facultyId=" +
        userInfo?.employeeId +
        "&month=" +
        month +
        "&year=" +
        year;
      GetBATCHDATA(url, () => {
        if (requestIndex === batchRequestRef.current) {
          setBatchDataLoaded(true);
        }
      });
    }
  }, [userInfo]);

  useEffect(() => {
    if (userInfo && userInfo.employeeId) {
      const facultyFeedbackUrl =
        BASE_URL +
        GET_FACULTY_PERFORMANCE_SEARCH +
        `?employeeId=${userInfo.employeeId}&yearMonth=${yearMonthForFeedback}`;
      GetFacultyFeedbackData(facultyFeedbackUrl);
    }
  }, [yearMonthForFeedback]);

  useEffect(() => {
    if (batchDataLoaded && selectedSession && filteredBatchData.length === 0) {
      ToastDispatch({
        isOpen: true,
        type: "Error",
        message: "No batches found for the selected session",
      });
    }
  }, [batchDataLoaded, selectedSession]);

  useEffect(() => {
    if (sessionsList.length === 0 && sessionsListFFetching === false) {
      ToastDispatch({
        isOpen: true,
        type: "Error",
        message: "No session found",
      });
    }
  }, [sessionsList, sessionsListFFetching]);

  const handleMarkSyllbus = (cardViewData: any) => {
    navigate("/admin/schedule", {
      replace: false,
      state: {
        isTodaySchedule: true,
        dateRanges: dateRanges,
        batchDetail: cardViewData,
      },
    });
  };
  useEffect(() => {
    if (userInfo.employeeId) {
      const employeeId = userInfo.employeeId;
      let newUrl = SYLLABUS_COMPLIANCE_LIST + `?facultyId=${employeeId}`;
      const startDate = format(
        setSeconds(
          setMinutes(
            setHours(subDays(new Date(dateRanges[0].startDate), 1), 18),
            30
          ),
          0
        ),
        "yyyy-MM-dd HH:mm:ss"
      );
      const endStart = format(
        setSeconds(
          setMinutes(setHours(new Date(dateRanges[0].endDate), 18), 29),
          0
        ),
        "yyyy-MM-dd HH:mm:ss"
      );
      newUrl =
        newUrl + `&startDate=${startDate}&endDate=${endStart}&eventType=ALL`;

      fetchData(BASE_URL + newUrl, newUrl, employeeId);
    }
  }, [userInfo]);
  const fetchData = async (url: string, newUrl: string, employeeId: any) => {
    setLoading(true);
    await getAPIRequest({
      url: url,
      success: (response: any) => {
        const data = response?.data?.data?.finalResponseMap;
        setTodaySchedules(data?.[todayDate] ?? []);
        setTimeout(() => {
          setLoading(false);
        }, 300);
      },
      failure: (error: any) => {
        if (
          error &&
          error["response"] &&
          error["response"]["data"] &&
          error["response"]["data"]["internalCode"] &&
          error["response"]["data"]["internalCode"] != "1008"
        ) {
          ToastDispatch({
            isOpen: true,
            type: "Error",
            message: error.message,
          });
        } else if (
          error &&
          error["response"] &&
          error["response"]["data"] &&
          error["response"]["data"]["code"] == "NF" &&
          employeeId != userInfo?.employeeId
        ) {
          ToastDispatch({
            isOpen: true,
            type: "Error",
            message: "No record found for searched faculty",
          });
        }
        setTodaySchedules([]);
        setTimeout(() => {
          setLoading(false);
        }, 300);
      },
    } as getRequestInterface);
  };
  return (
    <>
      {loading ? (
        <Spinner color="info"></Spinner>
      ) : (
        <>
          <Col md="12">
            <LectureCountCard
              screen={screen}
              totalUnmarkedLectures={sessionUnmarkedLectureCount}
              batchCount={filteredBatchData?.length}
              navigate={navigate}
              userInfo={userInfo}
              facultyFeedbackData={facultyFeedbackData}
              setYearMonthForFeedback={setYearMonthForFeedback}
              dateFilterValues={dateFilterValues}
              facultyFeedbackHomeData={facultyFeedbackHomeData}
              sessionsList={sessionsList}
              selectedSession={selectedSession}
              setSelectedSession={setSelectedSession}
            ></LectureCountCard>
          </Col>
          <Col md="12">
            <TodayScheduledLectureMain
              scheduleData={todaySchedules}
              handleMarkSyllbus={handleMarkSyllbus}
              dateRanges={dateRanges2}
            ></TodayScheduledLectureMain>
          </Col>
          <Col md="12">
            {isBatchLoader ? (
              <div className="d-flex justify-content-center align-items-center">
                <Spinner color="info"></Spinner>
              </div>
            ) : (
              <Card style={{ marginBottom: "16px" }}>
                <div className="d-flex justify-content-between pl-3 pt-3 pr-3">
                  <Typography
                    variant={["xs", "sm", "md"].includes(screen) ? "h6" : "h6F"}
                    color="black"
                  >
                    {"My Batches"}
                  </Typography>
                  <span
                    className="filterSpan"
                    onClick={() => {
                      pushToDataLayerForGTM({
                        event: "ag_home_screen_dashboard_my_batches_clicked",
                        USER_ROLE: userInfo?.fexRole || "",
                        EMPLOYEE_ID: userInfo?.employeeId || "",
                      });
                      navigate("/admin/batches");
                    }}
                  >
                    View All
                  </span>
                </div>
                <div className="p-2">
                  <TableLayoutAntd isLoader={loading}>
                    <MyBatchesTableAntd
                      data={filteredBatchData}
                      access={[
                        "batchName",
                        "plLecStatus",
                        "testType",
                        "testSyllbStatus",
                        "unMarkedLectureCount",
                      ]}
                      setData={setBATCHData}
                    ></MyBatchesTableAntd>
                  </TableLayoutAntd>
                </div>
              </Card>
            )}
          </Col>
        </>
      )}
    </>
  );
};
export default DashboardBatchesMain;
